<template>
  <div class="analytics">
    <!-- 看板筛选：时间范围 + 批次 + 分组（导出与看板共用同一筛选，口径一致） -->
    <el-card class="chart-card">
      <div class="filter-bar" style="display: flex; align-items: center; flex-wrap: wrap;">
        <el-date-picker
          v-model="filterDateRange"
          type="daterange"
          :range-separator="$t('analytics.rangeSep')"
          :start-placeholder="$t('analytics.rangeStart')"
          :end-placeholder="$t('analytics.rangeEnd')"
          value-format="yyyy-MM-dd"
          style="margin-right: 12px;"
        ></el-date-picker>
        <el-select v-model="filterBatchId" :placeholder="$t('analytics.filterBatch')" clearable style="width: 170px; margin-right: 12px;">
          <el-option v-for="b in batchOptions" :key="b.id" :label="b.name" :value="b.id"></el-option>
        </el-select>
        <el-select v-model="filterGroupId" :placeholder="$t('analytics.filterGroup')" clearable style="width: 170px; margin-right: 12px;">
          <el-option v-for="g in groupOptions" :key="g.id" :label="g.name" :value="g.id"></el-option>
        </el-select>
        <el-button-group style="margin-right: 12px;">
          <el-button size="small" :type="activeQuick === 7 ? 'primary' : ''" @click="applyQuickRange(7)">{{ $t('analytics.quick7') }}</el-button>
          <el-button size="small" :type="activeQuick === 30 ? 'primary' : ''" @click="applyQuickRange(30)">{{ $t('analytics.quick30') }}</el-button>
          <el-button size="small" :type="activeQuick === 0 ? 'primary' : ''" @click="applyQuickRange(0)">{{ $t('analytics.quickAll') }}</el-button>
        </el-button-group>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">{{ $t('analytics.queryBtn') }}</el-button>
        <el-button icon="el-icon-refresh" @click="resetFilter">{{ $t('analytics.resetBtn') }}</el-button>
      </div>
    </el-card>

    <!-- 统计卡片
         用 CSS Grid 而非 el-col 的 12 栅格：6 张卡无法被 24 整除，
         硬套 span 会留出空白或折成第二行。auto-fit + minmax 让卡片等宽自适应，窄屏自动换行。 -->
    <div class="stat-row">
      <!-- 原「推送条数 / Total Pushes」卡片已于 2026-09-20 按运营要求移除：
           推送条数在她看来没有参考价值（含测试推送，且同一门课重复推也会累加） -->
      <el-card shadow="hover" class="stat-card">
        <div class="stat-value">{{ stats.totalViews || 0 }}</div>
        <div class="stat-label">{{ $t('analytics.statTotalViews') }}</div>
      </el-card>
      <el-card shadow="hover" class="stat-card">
        <div class="stat-value">{{ stats.activeUsers || 0 }}</div>
        <div class="stat-label">{{ $t('analytics.statActiveUsers') }}</div>
      </el-card>
      <!-- VJ 录音总数：2026-09-20 按运营要求替换原「内容总数」卡片
           （她认为课程总数无用，更关心用户有没有做语音日记） -->
      <el-card shadow="hover" class="stat-card">
        <div class="stat-value">{{ stats.vjCount || 0 }}</div>
        <div class="stat-label">{{ $t('analytics.statVjCount') }}</div>
      </el-card>
      <el-card shadow="hover" class="stat-card">
        <div class="stat-value">{{ formatDuration(stats.totalDuration || 0) }}</div>
        <div class="stat-label">{{ $t('analytics.statDuration') }}</div>
      </el-card>
      <el-card shadow="hover" class="stat-card">
        <el-tooltip placement="top" effect="dark">
          <div slot="content" style="max-width: 320px; line-height: 1.6;">{{ $t('analytics.statCompletedTip') }}</div>
          <div>
            <div class="stat-value">{{ stats.completedCount || 0 }}</div>
            <div class="stat-label">{{ $t('analytics.statCompleted') }}</div>
            <!-- 补上分母：这个数是「学员-课程」数，不是课程数。
                 不显示分母时，运营只能看到 36 和 78%，无法互相印证，只能猜 -->
            <div class="stat-sub" v-if="stats.pushedPairs">{{ $t('analytics.ofPushed', { n: stats.pushedPairs }) }}</div>
          </div>
        </el-tooltip>
      </el-card>
      <el-card shadow="hover" class="stat-card">
        <el-tooltip placement="top" effect="dark">
          <div slot="content" style="max-width: 320px; line-height: 1.6;">
            <div>{{ $t('analytics.completionRateTip') }}</div>
            <!-- 算式「36 ÷ 47」改为悬停才显示（2026-09-20 运营要求）：平时不占卡片版面。
                 分母是「推出去的学员-课程数」，不是「打开过的」。
                 ⚠ 这里必须用内联样式：el-tooltip 的内容渲染到 body 上的 popper 里，
                 scoped 样式够不着。 -->
            <div
              v-if="stats.pushedPairs"
              style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255, 255, 255, 0.25); font-weight: 600;"
            >
              {{ (stats.completedCount || 0) + ' ÷ ' + stats.pushedPairs }}
            </div>
          </div>
          <div>
            <div class="stat-value">{{ stats.completionRate || '0%' }}</div>
            <div class="stat-label">{{ $t('analytics.statCompletionRate') }}</div>
          </div>
        </el-tooltip>
      </el-card>
    </div>

    <!-- 内容类型统计 -->
    <el-card class="chart-card">
      <div slot="header" class="card-header">
        <span>{{ $t('analytics.typeStatsTitle') }}</span>
      </div>
      <el-table :data="contentTypeStats" border stripe>
        <el-table-column prop="type" :label="$t('analytics.colType')" width="120">
          <template slot-scope="scope">
            {{ getTypeName(scope.row.type) }}
          </template>
        </el-table-column>
        <!-- Received：该周期推出去的课程数（按课程去重，不按人）—— 2026-09-20 按运营要求改口径 -->
        <el-table-column prop="received" width="130">
          <template slot="header">
            <span>{{ $t('analytics.colReceived') }}</span>
            <!-- 两行互不排斥：一门课可能同时有文本版和视频版。
                 不解释的话，运营会看到「17 + 12 = 29」而困惑为什么加起来比课程总数多 -->
            <el-tooltip :content="$t('analytics.receivedTip')" placement="top">
              <i class="el-icon-question" style="margin-left: 4px; color: #c0c4cc;"></i>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="viewCount" :label="$t('analytics.colViewCount')" width="100"></el-table-column>
        <el-table-column :label="$t('analytics.colRatio')" width="300">
          <template slot-scope="scope">
            <el-progress :percentage="scope.row.percentage || 0" :color="getProgressColor(scope.row.percentage)"></el-progress>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 课程完成度（视频或图文任一达标即算完成） -->
    <el-card class="chart-card">
      <div slot="header"><span class="section-title">{{ $t('analytics.courseCompletion') }}</span></div>
      <el-table :data="courseCompletionStats" border stripe>
        <el-table-column prop="range" :label="$t('analytics.colRange')" width="150"></el-table-column>
        <el-table-column prop="count" :label="$t('analytics.colTimes')" width="80"></el-table-column>
        <el-table-column :label="$t('analytics.colDist')">
          <template slot-scope="scope">
            <el-progress :percentage="scope.row.percentage || 0" :color="getProgressColor(scope.row.percentage)"></el-progress>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 内容阅读排行（2026-09-20：改为按总时长排序 + 分页，不再是固定 TOP 20） -->
    <el-card class="chart-card">
      <div slot="header" class="card-header">
        <span>{{ $t('analytics.topContent') }}</span>
        <span class="sort-hint">{{ $t('analytics.sortByDuration') }}</span>
      </div>
      <el-table :data="contentTopList.list" border stripe>
        <el-table-column type="index" :label="$t('analytics.colRank')" width="60"></el-table-column>
        <el-table-column prop="title" :label="$t('analytics.colContentTitle')"></el-table-column>
        <!-- 排序依据（总时长）放在浏览次数前面，避免运营觉得顺序对不上 -->
        <el-table-column prop="totalDuration" :label="$t('analytics.colTotalDuration')" width="130">
          <template slot-scope="scope">
            {{ formatDuration(scope.row.totalDuration || 0) }}
          </template>
        </el-table-column>
        <el-table-column prop="viewCount" :label="$t('analytics.colViewCount')" width="110"></el-table-column>
        <el-table-column prop="avgCompletion" :label="$t('analytics.colAvgCompletion')" width="130">
          <template slot-scope="scope">
            <el-progress :percentage="scope.row.avgCompletion || 0" :stroke-width="10"></el-progress>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="contentTopList.total > listPageSize"
        class="list-pagination"
        layout="total, prev, pager, next"
        small
        :current-page="contentPage"
        :page-size="listPageSize"
        :total="contentTopList.total"
        @current-change="handleContentPageChange"
      ></el-pagination>
    </el-card>

    <!-- 用户阅读排行（2026-09-20：改为按总时长排序 + 分页，不再是固定 TOP 10） -->
    <el-card class="chart-card">
      <div slot="header" class="card-header">
        <span>{{ $t('analytics.topUsers') }}</span>
        <span class="sort-hint">{{ $t('analytics.sortByDuration') }}</span>
      </div>
      <el-table :data="userTopList.list" border stripe>
        <el-table-column type="index" :label="$t('analytics.colRank')" width="60"></el-table-column>
        <el-table-column prop="nickname" :label="$t('analytics.colNickname')"></el-table-column>
        <!-- 排序依据（总时长）放在浏览次数前面，与内容排行保持一致 -->
        <el-table-column prop="totalDuration" :label="$t('analytics.colTotalDuration')" width="130">
          <template slot-scope="scope">
            {{ formatDuration(scope.row.totalDuration || 0) }}
          </template>
        </el-table-column>
        <el-table-column prop="viewCount" :label="$t('analytics.colViewCount')" width="110"></el-table-column>
        <el-table-column prop="completedCount" :label="$t('analytics.colCompletedCount')" width="110"></el-table-column>
        <el-table-column prop="lastActiveAt" :label="$t('analytics.colLastActive')" width="170"></el-table-column>
      </el-table>
      <el-pagination
        v-if="userTopList.total > listPageSize"
        class="list-pagination"
        layout="total, prev, pager, next"
        small
        :current-page="userPage"
        :page-size="listPageSize"
        :total="userTopList.total"
        @current-change="handleUserPageChange"
      ></el-pagination>
    </el-card>

    <!-- 导出按钮（口径与看板筛选一致：导出的就是页面上看到的） -->
    <div class="export-bar">
      <span style="margin-right: 16px; color: #909399; font-size: 13px;">{{ $t('analytics.exportHint') }}</span>
      <el-switch
        v-model="excludeZero"
        :active-text="$t('analytics.excludeZeroActive')"
        :inactive-text="$t('analytics.excludeZeroInactive')"
        style="margin-right: 16px;"
      ></el-switch>
      <el-button type="success" icon="el-icon-download" :loading="exporting.user" @click="handleExport">{{ $t('analytics.exportUser') }}</el-button>
      <el-button type="primary" icon="el-icon-download" style="margin-left: 12px;" :loading="exporting.tweet" @click="handleTweetExport">{{ $t('analytics.exportTweet') }}</el-button>
      <el-button type="warning" icon="el-icon-download" style="margin-left: 12px;" :loading="exporting.detail" @click="handleReadDetailExport">{{ $t('analytics.exportDetail') }}</el-button>
    </div>
  </div>
</template>

<script>
import { getStats, exportExcel, exportTweetExcel, exportReadDetailExcel } from '@/api/analytics'
import { getList as getBatchList } from '@/api/userBatch'
import { getList as getGroupList } from '@/api/userGroup'
import { saveAs } from 'file-saver'

export default {
  name: 'Analytics',
  data() {
    return {
      stats: {},
      contentTypeStats: [],
      courseCompletionStats: [],
      videoCompletionStats: [],
      textCompletionStats: [],
      // 两个排行改为分页（2026-09-20）：结构为 { list, total }，各自独立翻页
      contentTopList: { list: [], total: 0 },
      userTopList: { list: [], total: 0 },
      contentPage: 1,
      userPage: 1,
      listPageSize: 10,
      // 看板筛选（导出共用同一套，保证口径一致）
      filterDateRange: null,
      filterBatchId: null,
      filterGroupId: null,
      activeQuick: 0,   // 0=全部 7/30=近N天
      batchOptions: [],
      groupOptions: [],
      // 导出过滤：排除 0 秒且无 PDF 交互的无效浏览记录（对齐详情页「隐藏0秒记录」开关）
      excludeZero: false,
      exporting: {
        user: false,
        tweet: false,
        detail: false
      }
    }
  },
  created() {
    this.loadFilterOptions()
    this.loadData()
  },
  methods: {
    formatDuration(seconds) {
      if (!seconds || seconds <= 0) return this.$t('userData.durationSecond', { n: 0 })
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = Math.floor(seconds % 60)
      let result = ''
      if (h > 0) result += this.$t('userData.durationHour', { n: h })
      if (m > 0) result += this.$t('userData.durationMinute', { n: m })
      if (s > 0) result += this.$t('userData.durationSecond', { n: s })
      return result || this.$t('userData.durationSecond', { n: 0 })
    },
    // 组装看板/导出共用的筛选参数（口径一致的关键）
    buildFilterParams() {
      const params = {}
      if (this.filterDateRange && this.filterDateRange.length === 2) {
        params.start_date = this.filterDateRange[0]
        params.end_date = this.filterDateRange[1]
      }
      if (this.filterBatchId) params.batch_id = this.filterBatchId
      if (this.filterGroupId) params.group_id = this.filterGroupId
      return params
    },
    // 加载筛选下拉选项（批次/分组）
    loadFilterOptions() {
      getBatchList().then(res => {
        this.batchOptions = res.data || []
      }).catch(() => {})
      getGroupList().then(res => {
        this.groupOptions = res.data || []
      }).catch(() => {})
    },
    // 快捷时间范围：days=0 表示全部
    applyQuickRange(days) {
      this.activeQuick = days
      if (!days) {
        this.filterDateRange = null
      } else {
        const fmt = d => {
          const y = d.getFullYear()
          const m = String(d.getMonth() + 1).padStart(2, '0')
          const day = String(d.getDate()).padStart(2, '0')
          return `${y}-${m}-${day}`
        }
        const end = new Date()
        const start = new Date()
        start.setDate(start.getDate() - days + 1)
        this.filterDateRange = [fmt(start), fmt(end)]
      }
      this.handleSearch()
    },
    // 重置筛选
    resetFilter() {
      this.filterDateRange = null
      this.filterBatchId = null
      this.filterGroupId = null
      this.activeQuick = 0
      this.handleSearch()
    },
    // 筛选变化时回到第一页并重新加载。
    // 必须先归零：否则改完筛选后可能停在一个新结果里并不存在的页码上，看到空表
    handleSearch() {
      this.contentPage = 1
      this.userPage = 1
      this.loadData()
    },
    // 内容排行翻页（只改内容页码，不影响用户排行）
    handleContentPageChange(page) {
      this.contentPage = page
      this.loadData()
    },
    // 用户排行翻页（只改用户页码，不影响内容排行）
    handleUserPageChange(page) {
      this.userPage = page
      this.loadData()
    },
    // 加载分析数据
    loadData() {
      const params = this.buildFilterParams()
      // 两个排行各自的分页参数（筛选条件共用，页码独立）
      params.content_page = this.contentPage
      params.user_page = this.userPage
      params.page_size = this.listPageSize
      getStats(params).then(res => {
        this.stats = res.data.stats || {}
        this.contentTypeStats = res.data.contentTypeStats || []
        this.courseCompletionStats = res.data.courseCompletionStats || []
        this.videoCompletionStats = res.data.videoCompletionStats || []
        this.textCompletionStats = res.data.textCompletionStats || []
        this.contentTopList = res.data.contentTopList || { list: [], total: 0 }
        this.userTopList = res.data.userTopList || { list: [], total: 0 }
      }).catch(() => {
        this.$message.error(this.$t('analytics.loadFail'))
      })
    },
    // 获取类型名称
    getTypeName(type) {
      const map = { video: this.$t('analytics.typeVideo'), text: this.$t('analytics.typeText'), article: this.$t('analytics.typeArticle') }
      return map[type] || type
    },
    // 进度条颜色
    getProgressColor(percentage) {
      if (percentage < 30) return '#F56C6C'
      if (percentage < 70) return '#E6A23C'
      return '#67C23A'
    },
    // 获取导出参数：与看板筛选完全一致（时间/批次/分组），另加明细特有的 excludeZero
    getExportParams() {
      const params = this.buildFilterParams()
      // 仅在开关打开时透传，避免默认请求带无意义参数
      if (this.excludeZero) {
        params.exclude_zero = 1
      }
      // 导出语言跟随当前界面语言
      params.locale = this.$getLang() || 'zh'
      return params
    },
    // 通用导出处理（允许不选任何筛选 = 导出全部）
    doExport(type, exportFn, filename, loadingKey) {
      this.exporting[loadingKey] = true
      const params = this.getExportParams()
      exportFn(params).then(res => {
        // res 已是 Blob（responseType: 'blob'）。若后端返回错误 JSON，其 MIME 为 application/json，
        // 需提示真实错误，而不是下载一个损坏的 xlsx 文件
        if (res && res.type && res.type.indexOf('json') !== -1) {
          return res.text().then(text => {
            let msg = this.$t('analytics.exportFail')
            try {
              const data = JSON.parse(text)
              msg = data.msg || msg
            } catch (e) {
              // 解析失败则使用默认错误信息
            }
            this.$message.error(msg)
          })
        }
        saveAs(res, filename + '_' + new Date().toISOString().slice(0, 10) + '.xlsx')
        this.$message.success(this.$t('analytics.exportSuccess'))
      }).catch(() => {
        this.$message.error(this.$t('analytics.exportFailAdmin'))
      }).finally(() => {
        this.exporting[loadingKey] = false
      })
    },
    // 导出用户阅读情况 Excel
    handleExport() {
      this.doExport('user', exportExcel, this.$t('analytics.exportUserFilename'), 'user')
    },
    // 导出推文数据 Excel
    handleTweetExport() {
      this.doExport('tweet', exportTweetExcel, this.$t('analytics.exportTweetFilename'), 'tweet')
    },
    // 导出阅读明细数据 Excel
    handleReadDetailExport() {
      this.doExport('detail', exportReadDetailExcel, this.$t('analytics.exportDetailFilename'), 'detail')
    }
  }
}
</script>

<style scoped>
.stat-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}
.stat-card {
  text-align: center;
  padding: 10px 0;
}
.stat-value {
  font-size: 22px;
  font-weight: bold;
  color: #303133;
}
.stat-label {
  font-size: 13px;
  color: #909399;
  margin-top: 5px;
}
/* 卡片副行：完课数卡片的「共推送 N」分母提示。
   运营反复追问「87% 是哪个除以哪个」「55 在哪能数出来」，根因就是这个数没显示。
   完课率的算式「36 ÷ 47」已改为悬停才显示，不再常驻（2026-09-20 运营要求） */
.stat-sub {
  font-size: 12px;
  color: #c0c4cc;
  margin-top: 3px;
}
.chart-card {
  margin-bottom: 20px;
}
/* 排行表：分页右对齐 + 表头的排序依据提示 */
.list-pagination {
  margin-top: 12px;
  text-align: right;
}
.sort-hint {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
}
.card-header {
  font-weight: bold;
  font-size: 16px;
}
.section-title {
  font-weight: bold;
  font-size: 15px;
}
.export-bar {
  text-align: center;
  margin: 20px 0;
}
</style>
